import { useEffect, useState } from "react";
import axios from "axios";
import {
  Plus, Rows3, Trash2, Ticket, X, Mail, RefreshCw, Download, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_VALUE = 20;

/**
 * Vouchere tab — CRUD manual pentru codurile de discount oferite
 * clienților pentru recenzii. Atribuirea la client se face din butonul
 * WhatsApp în conversație (implementat separat). Aici doar liste +
 * adăugare (unul / mai multe) + ștergere pentru cei încă disponibili.
 */
export default function Vouchere() {
  const [data, setData] = useState({ vouchers: [], totals: { disponibil: {count:0,value:0}, oferit: {count:0,value:0} }, count: 0 });
  const [loading, setLoading] = useState(false);
  const [fStatus, setFStatus] = useState("");
  const [modalSingle, setModalSingle] = useState(false);
  const [modalBulk, setModalBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/vouchers`, { params: fStatus ? { status: fStatus } : {} });
      setData(r.data || { vouchers: [] });
      // Curăță selecția pentru codurile care nu mai există (ex. după bulk delete).
      setSelected((prev) => {
        const codes = new Set((r.data?.vouchers || []).map((v) => v.code));
        const next = new Set();
        prev.forEach((c) => { if (codes.has(c)) next.add(c); });
        return next;
      });
    } catch (e) {
      toast.error("Nu am putut încărca voucherele", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fStatus]);

  const del = async (code) => {
    if (!window.confirm(`Șterg voucher ${code}?`)) return;
    try {
      await axios.delete(`${API}/apeluri-recenzii/vouchers/${encodeURIComponent(code)}`);
      toast.success("Șters");
      load();
    } catch (e) { toast.error("Ștergere eșuată", { description: e.response?.data?.detail || e.message }); }
  };

  const toggleSelect = (code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = data.vouchers.filter((v) => v.status === "disponibil").map((v) => v.code);
    setSelected((prev) => {
      const allSelected = selectable.every((c) => prev.has(c));
      return new Set(allSelected ? [] : selectable);
    });
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Șterg ${selected.size} vouchere selectate?\n\n(Doar cele disponibile — cele oferite sunt ignorate.)`)) return;
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/vouchers/bulk-delete`, { codes: Array.from(selected) });
      toast.success(`${r.data?.deleted || 0} vouchere șterse`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error("Ștergere eșuată", { description: e.response?.data?.detail || e.message });
    }
  };

  const exportXlsx = async () => {
    if (selected.size === 0) return;
    try {
      const r = await axios.post(
        `${API}/apeluri-recenzii/vouchers/export-xlsx`,
        { codes: Array.from(selected) },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vouchere_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selected.size} vouchere exportate`);
    } catch (e) {
      toast.error("Export eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  // Feb 2026 — schimbare manuală status voucher între Disponibil / Oferit.
  // Update optimist pe rând, rollback la eroare (revenim la vechiul status).
  const changeStatus = async (code, newStatus) => {
    const prev = data.vouchers.find((v) => v.code === code);
    if (!prev || prev.status === newStatus) return;
    if (newStatus === "disponibil" && prev.status === "oferit") {
      if (!window.confirm(`Marchezi ${code} ca Disponibil?\n\n(Se pierd datele clientului atribuit.)`)) return;
    }
    // Optimistic UI
    setData((d) => ({
      ...d,
      vouchers: d.vouchers.map((v) =>
        v.code === code
          ? {
              ...v,
              status: newStatus,
              ...(newStatus === "disponibil"
                ? { assigned_order_id: null, assigned_customer_name: null, assigned_phone: null }
                : {}),
            }
          : v,
      ),
    }));
    try {
      await axios.patch(`${API}/apeluri-recenzii/vouchers/${encodeURIComponent(code)}/status`, { status: newStatus });
      toast.success(`${code} → ${newStatus === "disponibil" ? "Disponibil" : "Oferit"}`);
      // Reload ca să refacem totalurile + selecția
      setSelected((s) => {
        const n = new Set(s);
        n.delete(code);
        return n;
      });
      load();
    } catch (e) {
      toast.error("Schimbare status eșuată", { description: e.response?.data?.detail || e.message });
      load();
    }
  };

  const selectableCount = data.vouchers.filter((v) => v.status === "disponibil").length;
  const allSelected = selectableCount > 0 && selectableCount === data.vouchers.filter((v) => v.status === "disponibil" && selected.has(v.code)).length;

  return (
    <div className="space-y-4" data-testid="vouchere-tab">
      {/* Email accounts card (multi-account, manual "Extrage acum") */}
      <VoucherEmailAccountsCard onImported={load} />
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <button
            data-testid="add-single-voucher"
            onClick={() => setModalSingle(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-400"
          >
            <Plus className="h-3.5 w-3.5" /> Adaugă un voucher
          </button>
          <button
            data-testid="add-bulk-voucher"
            onClick={() => setModalBulk(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-sm text-orange-100 hover:bg-orange-500/20"
          >
            <Rows3 className="h-3.5 w-3.5" /> Adaugă mai multe
          </button>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-200">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            {data.totals.disponibil.count} disponibile · {data.totals.disponibil.value.toFixed(2)} RON
          </span>
          <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-rose-200">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
            {data.totals.oferit.count} oferite · {data.totals.oferit.value.toFixed(2)} RON
          </span>
          <select
            data-testid="filter-voucher-status"
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
            className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none"
          >
            <option value="">Toate statusurile</option>
            <option value="disponibil">Disponibile</option>
            <option value="oferit">Oferite</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar — visible when at least one voucher is selected */}
      {selected.size > 0 && (
        <div
          className="flex items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-100"
          data-testid="voucher-bulk-bar"
        >
          <span data-testid="voucher-selected-count">
            {selected.size} selectat{selected.size === 1 ? "" : "e"}
          </span>
          <button
            type="button"
            onClick={bulkDelete}
            data-testid="voucher-bulk-delete"
            className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-rose-100 hover:bg-rose-500/20"
          >
            <Trash2 className="h-3 w-3" /> Șterge selectate
          </button>
          <button
            type="button"
            onClick={exportXlsx}
            data-testid="voucher-bulk-export"
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-emerald-100 hover:bg-emerald-500/20"
          >
            <FileSpreadsheet className="h-3 w-3" /> Exportă în Excel
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-[11px] text-zinc-300 hover:text-zinc-100"
          >
            Deselectează tot
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900/70 text-[11px] uppercase text-zinc-500">
              <tr>
                <th className="w-8 px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-3.5 w-3.5 cursor-pointer accent-orange-500"
                    data-testid="voucher-select-all"
                    disabled={selectableCount === 0}
                    title="Selectează toate disponibile"
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium">Cod</th>
                <th className="px-3 py-2 text-left font-medium">Valoare</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Client (dacă oferit)</th>
                <th className="px-3 py-2 text-left font-medium">Comandă</th>
                <th className="px-3 py-2 text-left font-medium">Telefon</th>
                <th className="px-3 py-2 text-right font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading && data.vouchers.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">Se încarcă…</td></tr>
              )}
              {!loading && data.vouchers.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">Niciun voucher.</td></tr>
              )}
              {data.vouchers.map((v) => (
                <tr key={v.code} className="border-t border-zinc-900" data-testid={`voucher-row-${v.code}`}>
                  <td className="px-2 py-2 text-center">
                    {v.status === "disponibil" ? (
                      <input
                        type="checkbox"
                        checked={selected.has(v.code)}
                        onChange={() => toggleSelect(v.code)}
                        className="h-3.5 w-3.5 cursor-pointer accent-orange-500"
                        data-testid={`voucher-select-${v.code}`}
                      />
                    ) : (
                      <span className="text-zinc-700" title="Oferit — nu poate fi selectat">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-100">{v.code}</td>
                  <td className="px-3 py-2 text-zinc-200">{Number(v.value_ron).toFixed(2)} RON</td>
                  <td className="px-3 py-2">
                    <select
                      value={v.status}
                      onChange={(e) => changeStatus(v.code, e.target.value)}
                      data-testid={`voucher-status-${v.code}`}
                      className={`cursor-pointer rounded border-0 px-2 py-0.5 text-[11px] font-medium focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                        v.status === "disponibil"
                          ? "bg-emerald-500/10 text-emerald-300"
                          : "bg-rose-500/10 text-rose-300"
                      }`}
                      title="Click pentru a schimba manual statusul"
                    >
                      <option value="disponibil" className="bg-zinc-900 text-emerald-300">
                        ● Disponibil
                      </option>
                      <option value="oferit" className="bg-zinc-900 text-rose-300">
                        ● Oferit
                      </option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{v.assigned_customer_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-orange-300">{v.assigned_order_id || "—"}</td>
                  <td className="px-3 py-2 font-mono text-sky-300">{v.assigned_phone || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {v.status === "disponibil" && (
                      <button
                        onClick={() => del(v.code)}
                        data-testid={`delete-voucher-${v.code}`}
                        className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-300"
                        title="Șterge"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalSingle && <SingleVoucherModal onClose={() => setModalSingle(false)} onSaved={load} />}
      {modalBulk && <BulkVoucherModal onClose={() => setModalBulk(false)} onSaved={load} />}
    </div>
  );
}

function SingleVoucherModal({ onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [value, setValue] = useState(DEFAULT_VALUE);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!code.trim()) { toast.error("Cod gol"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/apeluri-recenzii/vouchers`, { code: code.trim(), value_ron: Number(value) || DEFAULT_VALUE });
      toast.success("Voucher adăugat");
      onSaved?.(); onClose();
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Adaugă un voucher" onClose={onClose} testId="single-voucher-modal">
      <div className="space-y-2">
        <label className="block">
          <span className="text-[11px] uppercase text-zinc-500">Cod voucher</span>
          <input
            data-testid="single-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ex: BRAG-XYZ123"
            className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-700 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase text-zinc-500">Valoare (RON)</span>
          <input
            data-testid="single-value"
            type="number" step="0.01" min="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-700 focus:outline-none"
          />
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900">Renunță</button>
        <button
          data-testid="single-save"
          onClick={save}
          disabled={saving}
          className="rounded-md bg-orange-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-400 disabled:opacity-60"
        >{saving ? "Salvez…" : "Salvează"}</button>
      </div>
    </ModalShell>
  );
}

function BulkVoucherModal({ onClose, onSaved }) {
  const [rows, setRows] = useState(Array.from({ length: 5 }, () => ({ code: "", value_ron: DEFAULT_VALUE })));
  const [saving, setSaving] = useState(false);

  const addRow = () => setRows((r) => [...r, { code: "", value_ron: DEFAULT_VALUE }]);
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const updateRow = (idx, key, value) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };
  // Bulk paste: dacă utilizatorul lipește N linii într-o celulă de cod, spargem în N rânduri.
  const onCodePaste = (idx, e) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\n")) return;
    e.preventDefault();
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    setRows((r) => {
      const next = [...r];
      lines.forEach((code, i) => {
        const slot = idx + i;
        if (slot < next.length) next[slot] = { ...next[slot], code };
        else next.push({ code, value_ron: DEFAULT_VALUE });
      });
      return next;
    });
  };

  const save = async () => {
    const items = rows.filter((r) => r.code.trim()).map((r) => ({
      code: r.code.trim(),
      value_ron: Number(r.value_ron) || DEFAULT_VALUE,
    }));
    if (items.length === 0) { toast.error("Nici un voucher completat"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/apeluri-recenzii/vouchers/bulk`, { items });
      const d = r.data;
      const msg = `Adăugate: ${d.inserted}${d.skipped_duplicates?.length ? ` · Duplicate sărite: ${d.skipped_duplicates.length}` : ""}${d.skipped_empty ? ` · Rânduri goale: ${d.skipped_empty}` : ""}`;
      toast.success(msg);
      onSaved?.(); onClose();
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Adaugă mai multe vouchere" onClose={onClose} testId="bulk-voucher-modal" wide>
      <p className="mb-2 text-[11px] text-zinc-500">
        Poți face copy/paste dintr-o coloană Excel (una pe linie) direct în primul câmp de cod — se vor completa automat rândurile. Valoare default {DEFAULT_VALUE} RON, modificabilă per rând.
      </p>
      <div className="max-h-[400px] overflow-y-auto rounded-md border border-zinc-800">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-zinc-900/95 text-[10px] uppercase text-zinc-500">
            <tr>
              <th className="w-8 px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">Cod voucher</th>
              <th className="w-32 px-2 py-1.5 text-left">Valoare (RON)</th>
              <th className="w-10 px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="border-t border-zinc-900">
                <td className="px-2 py-1 text-zinc-600">{idx + 1}</td>
                <td className="px-2 py-1">
                  <input
                    data-testid={`bulk-code-${idx}`}
                    value={r.code}
                    onChange={(e) => updateRow(idx, "code", e.target.value)}
                    onPaste={(e) => onCodePaste(idx, e)}
                    placeholder="Cod voucher"
                    className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 font-mono text-xs text-zinc-100 focus:border-zinc-600 focus:outline-none"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    data-testid={`bulk-value-${idx}`}
                    type="number" step="0.01" min="0.01"
                    value={r.value_ron}
                    onChange={(e) => updateRow(idx, "value_ron", e.target.value)}
                    className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-100 focus:border-zinc-600 focus:outline-none"
                  />
                </td>
                <td className="px-2 py-1 text-right">
                  <button onClick={() => removeRow(idx)} title="Șterge rând" className="rounded p-0.5 text-zinc-600 hover:bg-rose-500/10 hover:text-rose-300">
                    <X className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <button
          data-testid="bulk-add-row"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-900"
        >
          <Plus className="h-3 w-3" /> Adaugă rând
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900">Renunță</button>
          <button
            data-testid="bulk-save"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-orange-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-400 disabled:opacity-60"
          >{saving ? "Salvez…" : `Salvează ${rows.filter((r) => r.code.trim()).length} vouchere`}</button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, testId, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid={testId}>
      <div
        className={`w-full ${wide ? "max-w-3xl" : "max-w-md"} rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <Ticket className="h-4 w-4 text-orange-400" /> {title}
          </h3>
          <button onClick={onClose} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * VoucherEmailAccountsCard — multi-account IMAP setup pentru extragerea
 * automată a voucherelor eMAG din inbox-uri Gmail/Yahoo/Outlook. Pentru
 * fiecare cont există buton „Extrage acum" — nu rulează in background.
 * Se scanează DOAR mesajele NEcitite de la comenzi@emag.ro cu subiectul
 * „Ai primit un cadou cu milioane de idei!".
 */
function VoucherEmailAccountsCard({ onImported }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [scanning, setScanning] = useState(null); // account id in-flight

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/apeluri-recenzii/vouchers/email-accounts`);
      setAccounts(r.data?.items || []);
    } catch (e) {
      toast.error("Nu pot încărca conturile email", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const removeAcc = async (a) => {
    if (!window.confirm(`Șterg contul ${a.email}?`)) return;
    try {
      await axios.delete(`${API}/apeluri-recenzii/vouchers/email-accounts/${encodeURIComponent(a.id)}`);
      toast.success("Cont șters");
      load();
    } catch (e) {
      toast.error("Ștergere eșuată", { description: e.response?.data?.detail || e.message });
    }
  };

  const scan = async (a) => {
    setScanning(a.id);
    try {
      // Scan-ul IMAP poate procesa 100+ mail-uri într-un singur apel (login,
      // fetch, parsare HTML, mark \Seen per UID). Default-ul global e 25s —
      // insuficient. Setăm 5 minute per scan.
      const r = await axios.post(
        `${API}/apeluri-recenzii/vouchers/email-accounts/${encodeURIComponent(a.id)}/scan`,
        {},
        { timeout: 300000 },
      );
      const s = r.data || {};
      const parts = [`${s.inserted || 0} vouchere adăugate`];
      if (s.duplicates?.length) parts.push(`${s.duplicates.length} duplicate`);
      if (s.skipped_known) parts.push(`${s.skipped_known} deja procesate`);
      if (s.marked_seen) parts.push(`${s.marked_seen} mail-uri marcate citite`);
      toast.success(`Extragere OK: ${a.email}`, { description: parts.join(" · ") });
      onImported?.();
      load();
    } catch (e) {
      toast.error(`Extragere eșuată: ${a.email}`, { description: e.response?.data?.detail || e.message });
      load();
    } finally { setScanning(null); }
  };

  return (
    <div className="rounded-xl border border-sky-500/30 bg-zinc-950 p-4" data-testid="voucher-mail-accounts">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-sky-300" />
          <h2 className="text-sm font-semibold text-zinc-100">Conturi email pentru extragere vouchere</h2>
          <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400" data-testid="voucher-accounts-count">
            {accounts.length}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          data-testid="voucher-add-account"
          className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-xs text-sky-100 hover:bg-sky-500/20"
        >
          <Plus className="h-3 w-3" /> Adaugă cont
        </button>
      </div>
      <p className="mb-3 text-[11px] leading-snug text-zinc-500">
        Extragere manuală per cont — apeși „Extrage acum" când vrei. Se procesează doar mesajele
        <strong> NEcitite</strong> de la <code>comenzi@emag.ro</code> cu subiectul „Ai primit un cadou cu milioane de idei".
        Mail-urile din care se extrage voucher-ul sunt marcate ca CITITE — la următorul scan nu se mai reproceszează.
      </p>
      {loading && accounts.length === 0 && (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-4 text-center text-xs text-zinc-500">Se încarcă…</div>
      )}
      {!loading && accounts.length === 0 && (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-900/30 px-3 py-4 text-center text-xs text-zinc-500">
          Niciun cont adăugat. Apasă „Adaugă cont" pentru a conecta un Gmail/Yahoo.
        </div>
      )}
      <div className="space-y-2">
        {accounts.map((a) => {
          const lastResult = a.last_scan_result;
          return (
            <div
              key={a.id}
              data-testid={`voucher-account-${a.id}`}
              className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm text-zinc-100">
                  <span className="font-medium">{a.email}</span>
                  <span className="rounded border border-zinc-700 bg-zinc-800/60 px-1.5 py-0 text-[9px] uppercase tracking-wider text-zinc-400">
                    {a.provider}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500">
                  {a.imap_host}:{a.imap_port}
                  {a.last_scan_at && (
                    <> · ultima scanare: {new Date(a.last_scan_at).toLocaleString("ro-RO")}</>
                  )}
                  {lastResult?.inserted != null && (
                    <> · <span className="text-emerald-300">+{lastResult.inserted}</span></>
                  )}
                  {lastResult?.duplicates?.length > 0 && (
                    <> · <span className="text-amber-300">{lastResult.duplicates.length} dupl.</span></>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => scan(a)}
                disabled={scanning === a.id}
                data-testid={`voucher-account-scan-${a.id}`}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${scanning === a.id ? "animate-spin" : ""}`} />
                {scanning === a.id ? "Extrag…" : "Extrage acum"}
              </button>
              <button
                type="button"
                onClick={() => removeAcc(a)}
                data-testid={`voucher-account-delete-${a.id}`}
                title="Șterge cont"
                className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-300"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
      {showAdd && <AddMailAccountModal onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}

const PROVIDER_OPTIONS = [
  { key: "gmail",   label: "Gmail",   helpUrl: "https://support.google.com/accounts/answer/185833" },
  { key: "yahoo",   label: "Yahoo",   helpUrl: "https://help.yahoo.com/kb/generate-manage-third-party-passwords-sln15241.html" },
  { key: "outlook", label: "Outlook", helpUrl: "https://support.microsoft.com/en-us/account-billing/using-app-passwords-with-apps-that-don-t-support-two-step-verification-5896ed9b-4263-e681-128a-a6f2979a7944" },
];

function AddMailAccountModal({ onClose, onSaved }) {
  const [provider, setProvider] = useState("gmail");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const help = PROVIDER_OPTIONS.find((p) => p.key === provider);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password.trim()) {
      toast.error("Completează email + App Password");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/apeluri-recenzii/vouchers/email-accounts`, {
        provider, email: email.trim(), password: password.trim(),
      });
      toast.success("Cont adăugat");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error("Adăugare eșuată", { description: err.response?.data?.detail || err.message });
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="voucher-add-account-modal"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-3 rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">Adaugă cont email</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Provider</span>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            data-testid="voucher-mail-provider"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-sky-500/60 focus:outline-none"
          >
            {PROVIDER_OPTIONS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">Email</span>
          <input
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={provider === "gmail" ? "cineva@gmail.com" : provider === "yahoo" ? "cineva@yahoo.com" : "cineva@outlook.com"}
            data-testid="voucher-mail-email"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-sky-500/60 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-400">App Password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="16 caractere fără spații"
            data-testid="voucher-mail-password"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5 text-sm text-zinc-100 focus:border-sky-500/60 focus:outline-none"
          />
          {help && (
            <a
              href={help.helpUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-100"
            >
              <Download className="h-2.5 w-2.5" /> Cum obțin App Password pentru {help.label}?
            </a>
          )}
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-600"
          >
            Anulează
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="voucher-mail-save"
            className="inline-flex items-center gap-1 rounded-md border border-sky-500/50 bg-sky-500/90 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            {saving ? "Se salvează…" : "Salvează cont"}
          </button>
        </div>
      </form>
    </div>
  );
}

